import type { ReactNode } from "react";
import { useState } from "react";
import { Check, ChevronRight, Copy, Loader2 } from "lucide-react";

import { ConfirmDialog } from "@/components/dashboard/ConfirmDialog";
import { AppTopbar } from "@/components/layout/AppTopbar";
import { Sidebar } from "@/components/layout/Sidebar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { copyToClipboard } from "@/lib/helpers";
import { cn } from "@/lib/utils";
import { useAppStore } from "@/lib/store";

type EditableSection = "name" | "email" | "password" | "delete" | null;

export function SettingsPage() {
  const {
    user,
    updateDisplayName,
    updateEmail,
    updatePassword,
    deleteAccount,
    refreshUser,
  } = useAuth();
  const { dashboardSearchInput, setDashboardSearchInput } = useAppStore();

  const [activeSection, setActiveSection] = useState<EditableSection>(null);
  const [copiedId, setCopiedId] = useState(false);

  const [displayName, setDisplayName] = useState(user?.user_metadata?.display_name ?? "");
  const [nameLoading, setNameLoading] = useState(false);
  const [nameMessage, setNameMessage] = useState<string | null>(null);

  const [currentEmailInput, setCurrentEmailInput] = useState(user?.email ?? "");
  const [newEmail, setNewEmail] = useState("");
  const [emailPassword, setEmailPassword] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailMessage, setEmailMessage] = useState<string | null>(null);

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pwLoading, setPwLoading] = useState(false);
  const [pwMessage, setPwMessage] = useState<string | null>(null);

  const [showDeleteAccountConfirm, setShowDeleteAccountConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteMessage, setDeleteMessage] = useState<string | null>(null);

  const handleCopyId = async () => {
    if (!user?.id) return;
    const didCopy = await copyToClipboard(user.id);
    if (!didCopy) return;
    setCopiedId(true);
    window.setTimeout(() => setCopiedId(false), 1800);
  };

  const handleUpdateName = async () => {
    if (!displayName.trim()) return;
    setNameLoading(true);
    setNameMessage(null);

    const { error } = await updateDisplayName(displayName.trim());

    if (error) {
      setNameMessage(error);
    } else {
      await refreshUser();
      setNameMessage("Full name updated.");
      setActiveSection(null);
    }

    setNameLoading(false);
  };

  const handleUpdateEmail = async () => {
    if (!newEmail.trim() || !emailPassword) return;
    setEmailLoading(true);
    setEmailMessage(null);

    const result = await updateEmail(currentEmailInput, newEmail.trim(), emailPassword);
    setEmailMessage(result.message);

    if (result.success) {
      setCurrentEmailInput(newEmail.trim());
      setNewEmail("");
      setEmailPassword("");
      setActiveSection(null);
    }

    setEmailLoading(false);
  };

  const handleUpdatePassword = async () => {
    if (!newPassword || newPassword !== confirmPassword) {
      setPwMessage("Passwords do not match.");
      return;
    }

    if (newPassword.length < 6) {
      setPwMessage("Password must be at least 6 characters.");
      return;
    }

    setPwLoading(true);
    setPwMessage(null);

    const { error } = await updatePassword(newPassword);

    if (error) {
      setPwMessage(error);
    } else {
      setPwMessage("Password updated.");
      setNewPassword("");
      setConfirmPassword("");
      setActiveSection(null);
    }

    setPwLoading(false);
  };

  const handleDeleteAccount = async () => {
    setDeleteLoading(true);
    setDeleteMessage(null);

    const { error } = await deleteAccount();

    if (error) {
      setDeleteMessage(error);
      setDeleteLoading(false);
      setShowDeleteAccountConfirm(false);
      return;
    }

    window.location.assign("/");
  };

  return (
    <div className="flex min-h-screen flex-col bg-[#f6f5f6] text-[#1c171b] min-[992px]:flex-row">
      <Sidebar />

      <div className="flex-1 min-w-0">
        <AppTopbar
          title="Profile Settings"
          backTo="/app/dashboard/home"
          searchValue={dashboardSearchInput}
          onSearchChange={setDashboardSearchInput}
        />

        <main className="px-12 py-12 max-[991px]:px-[1.25rem] max-[991px]:py-[2rem]">
          <section className="font-[family-name:var(--font-ui)]">
            <div className="grid grid-cols-2 items-center border-b border-[#e2e4e9] py-5 max-[991px]:grid-cols-1">
              <div>
                <h2 className="m-0 text-[#1f0a18] text-lg font-medium leading-normal">User ID</h2>
                <p className="mt-[0.35rem] mb-0 text-[#525866] text-[0.85rem] font-normal">{user?.id ?? "Unavailable"}</p>
              </div>

              <div>
                <button
                  type="button"
                  className="inline-flex min-h-[2.6rem] items-center justify-center gap-2 border border-[#e2e4e9] rounded-[0.72rem] bg-[#fafafa] px-[0.95rem] text-[#525866] text-[0.85rem]"
                  onClick={() => void handleCopyId()}
                >
                  {copiedId ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  <span>{copiedId ? "Copied" : "Copy ID"}</span>
                </button>
              </div>
            </div>

            <SettingsEditableRow
              title="Full Name"
              description="Your name will be visible to your contacts."
              value={user?.user_metadata?.display_name ?? "Not set"}
              actionLabel="Edit"
              isActive={activeSection === "name"}
              onToggle={() =>
                setActiveSection((current) => (current === "name" ? null : "name"))
              }
            >
              <div className="grid max-w-[29rem] gap-4">
                <div className="grid gap-[0.45rem]">
                  <Label htmlFor="settings-name">Full Name</Label>
                  <Input
                    id="settings-name"
                    value={displayName}
                    className="h-[2.95rem] border-[#ddd9dc] rounded-[0.85rem] shadow-none"
                    onChange={(event) => setDisplayName(event.target.value)}
                  />
                </div>

                {nameMessage ? <p className="m-0 text-[#6e676d] text-[0.94rem]">{nameMessage}</p> : null}

                <button
                  type="button"
                  className="inline-flex min-h-[3rem] items-center justify-center gap-2 border border-[#8b2965] rounded-[0.9rem] bg-[#8b2965] px-[1.2rem] text-white text-[0.96rem] font-semibold whitespace-nowrap disabled:opacity-55"
                  disabled={nameLoading || !displayName.trim()}
                  onClick={() => void handleUpdateName()}
                >
                  {nameLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  <span>{nameLoading ? "Saving..." : "Save Name"}</span>
                </button>
              </div>
            </SettingsEditableRow>

            <SettingsEditableRow
              title="Email Address"
              description="Personal email address for recovery."
              value={user?.email ?? "Not set"}
              actionLabel="Edit"
              isActive={activeSection === "email"}
              onToggle={() =>
                setActiveSection((current) => (current === "email" ? null : "email"))
              }
            >
              <div className="grid max-w-[31rem] gap-4">
                <div className="grid gap-[0.45rem]">
                  <Label htmlFor="settings-current-email">Current Email</Label>
                  <Input
                    id="settings-current-email"
                    type="email"
                    value={currentEmailInput}
                    className="h-[2.95rem] border-[#ddd9dc] rounded-[0.85rem] shadow-none"
                    onChange={(event) => setCurrentEmailInput(event.target.value)}
                  />
                </div>

                <div className="grid gap-[0.45rem]">
                  <Label htmlFor="settings-new-email">New Email</Label>
                  <Input
                    id="settings-new-email"
                    type="email"
                    value={newEmail}
                    className="h-[2.95rem] border-[#ddd9dc] rounded-[0.85rem] shadow-none"
                    placeholder="name@example.com"
                    onChange={(event) => setNewEmail(event.target.value)}
                  />
                </div>

                <div className="grid gap-[0.45rem]">
                  <Label htmlFor="settings-email-password">Current Password</Label>
                  <Input
                    id="settings-email-password"
                    type="password"
                    value={emailPassword}
                    className="h-[2.95rem] border-[#ddd9dc] rounded-[0.85rem] shadow-none"
                    placeholder="Enter your current password"
                    onChange={(event) => setEmailPassword(event.target.value)}
                  />
                </div>

                {emailMessage ? (
                  <p className="m-0 text-[#6e676d] text-[0.94rem]">{emailMessage}</p>
                ) : null}

                <button
                  type="button"
                  className="inline-flex min-h-[3rem] items-center justify-center gap-2 border border-[#8b2965] rounded-[0.9rem] bg-[#8b2965] px-[1.2rem] text-white text-[0.96rem] font-semibold whitespace-nowrap disabled:opacity-55"
                  disabled={emailLoading || !newEmail.trim() || !emailPassword}
                  onClick={() => void handleUpdateEmail()}
                >
                  {emailLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  <span>{emailLoading ? "Updating..." : "Update Email"}</span>
                </button>
              </div>
            </SettingsEditableRow>

            <SettingsEditableRow
              title="Password"
              description="Security to login conveniently"
              value="************"
              actionLabel="Reset Password"
              isActive={activeSection === "password"}
              onToggle={() =>
                setActiveSection((current) => (current === "password" ? null : "password"))
              }
            >
              <div className="grid max-w-[31rem] gap-4">
                <div className="grid gap-[0.45rem]">
                  <Label htmlFor="settings-new-password">New Password</Label>
                  <Input
                    id="settings-new-password"
                    type="password"
                    value={newPassword}
                    className="h-[2.95rem] border-[#ddd9dc] rounded-[0.85rem] shadow-none"
                    placeholder="Minimum 6 characters"
                    onChange={(event) => setNewPassword(event.target.value)}
                  />
                </div>

                <div className="grid gap-[0.45rem]">
                  <Label htmlFor="settings-confirm-password">Confirm Password</Label>
                  <Input
                    id="settings-confirm-password"
                    type="password"
                    value={confirmPassword}
                    className="h-[2.95rem] border-[#ddd9dc] rounded-[0.85rem] shadow-none"
                    placeholder="Repeat your new password"
                    onChange={(event) => setConfirmPassword(event.target.value)}
                  />
                </div>

                {pwMessage ? <p className="m-0 text-[#6e676d] text-[0.94rem]">{pwMessage}</p> : null}

                <button
                  type="button"
                  className="inline-flex min-h-[3rem] items-center justify-center gap-2 border border-[#8b2965] rounded-[0.9rem] bg-[#8b2965] px-[1.2rem] text-white text-[0.96rem] font-semibold whitespace-nowrap disabled:opacity-55"
                  disabled={pwLoading || !newPassword || !confirmPassword}
                  onClick={() => void handleUpdatePassword()}
                >
                  {pwLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  <span>{pwLoading ? "Updating..." : "Update Password"}</span>
                </button>
              </div>
            </SettingsEditableRow>

            <SettingsEditableRow
              title="Delete Account"
              description=""
              value="This will permanently delete all the progress you have created"
              actionLabel="Delete Account"
              isActive={false}
              tone="danger"
              onToggle={() => setShowDeleteAccountConfirm(true)}
            >
              {null}
            </SettingsEditableRow>

            {deleteMessage ? (
              <p className="m-0 mt-2 text-[#ba4d4d] text-[0.94rem]">
                {deleteMessage}
              </p>
            ) : null}
          </section>
        </main>
      </div>

      <ConfirmDialog
        open={showDeleteAccountConfirm}
        title="Delete Account"
        description="Are you sure you want to delete your account? This action is permanent and cannot be undone. All your progress, performances, and data will be permanently removed."
        confirmLabel="Delete Account"
        tone="danger"
        loading={deleteLoading}
        onConfirm={() => void handleDeleteAccount()}
        onCancel={() => setShowDeleteAccountConfirm(false)}
      />
    </div>
  );
}

interface SettingsEditableRowProps {
  title: string;
  description: string;
  value: string;
  actionLabel: string;
  isActive: boolean;
  tone?: "default" | "danger";
  onToggle: () => void;
  children: ReactNode;
}

function SettingsEditableRow({
  title,
  description,
  value,
  actionLabel,
  isActive,
  tone = "default",
  onToggle,
  children,
}: SettingsEditableRowProps) {
  return (
    <div className="border-b border-[#e2e4e9] py-5">
      <div className="grid grid-cols-2 items-center max-[991px]:grid-cols-1 max-[991px]:gap-3">
        <div>
          <h2 className="m-0 text-[#1f0a18] text-lg font-medium leading-normal">{title}</h2>
          {description ? <p className="mt-[0.35rem] mb-0 text-[#525866] text-[0.85rem] font-normal leading-[1.5]">{description}</p> : null}
        </div>

        <div className="grid max-w-[16rem] justify-items-start gap-[0.35rem]">
          {value ? <p className="m-0 text-[#1f0a18] text-sm font-medium">{value}</p> : null}
          <button
            type="button"
            className={cn(
              "inline-flex items-center gap-1 text-[0.85rem] font-medium",
              tone === "danger" ? "text-[#f34264]" : "text-[#762a5c]"
            )}
            onClick={onToggle}
          >
            <span>{actionLabel}</span>
            <ChevronRight
              className={cn(
                "h-4 w-4 transition-transform duration-[180ms] ease-in-out",
                isActive && "rotate-90"
              )}
            />
          </button>
        </div>
      </div>

      {isActive ? <div className="pt-4">{children}</div> : null}
    </div>
  );
}
